"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { ApiWaveCreditType } from "@/generated/models/ApiWaveCreditType";
import { ApiWaveType } from "@/generated/models/ApiWaveType";
import type { ApiWaveCreditNft } from "@/generated/models/ApiWaveCreditNft";
import { CREATE_WAVE_VALIDATION_ERROR } from "@/helpers/waves/create-wave.validation";
import { WAVE_VOTING_LABELS } from "@/helpers/waves/waves.constants";
import CommonBorderedRadioButton from "@/components/utils/radio/CommonBorderedRadioButton";
import CreateWaveApprovalTiming, { CreateWaveApprovalTimingMode, getCreateWaveApprovalTimingMode } from "./CreateWaveApprovalTiming";
import CreateWaveVotingRep from "./CreateWaveVotingRep";
import CreateWaveVotingThreshold from "./CreateWaveVotingThreshold";
import CreateWaveVotingThresholdTime from "./CreateWaveVotingThresholdTime";
import MemeCardSetPicker from "./MemeCardSetPicker";
import MaxVotesPerIdentityInput from "./MaxVotesPerIdentityInput";
import NegativeVotingToggle from "./NegativeVotingToggle";
import TimeWeightedVoting from "./TimeWeightedVoting";
import type { TimeWeightedVotingConfig } from "./types";
const VOTING_TYPES_ORDER: Record<ApiWaveCreditType, number | undefined> = {
  [ApiWaveCreditType.TdhPlusXtdh]: 0,
  [ApiWaveCreditType.Tdh]: 1,
  [ApiWaveCreditType.Rep]: 2,
  [ApiWaveCreditType.CardSetTdh]: 3,
  [ApiWaveCreditType.Xtdh]: undefined
};
const TIME_WEIGHTED_DURATION_ERROR = "This interval is longer than the wave duration. Choose a shorter interval, extend the wave end date, or clear the end date.";
const APPROVAL_THRESHOLD_TIME_INVALID_ERROR = "Enter a whole number greater than 0, or leave blank for immediate approval.";
const APPROVAL_THRESHOLD_TIME_DURATION_ERROR = "This time is longer than the wave duration. Choose a shorter time, extend the wave end date, or clear the end date.";
const APPROVAL_TIMING_CONFLICT_ERROR = "Choose either minimum time above threshold or time-weighted voting.";
const DEFAULT_APPROVAL_THRESHOLD_TIME_MS = 60 * 1000;
const VOTING_SETTINGS_GRID_CLASSES = "tw-mt-6 tw-grid tw-grid-cols-1 tw-gap-3 tw-border-t tw-border-iron-700 tw-pt-6";
const VOTING_OPTIONS_GRID_CLASSES = "tw-mt-3 tw-grid tw-gap-3 sm:tw-grid-cols-2 lg:tw-grid-cols-4 [&>div]:tw-gap-x-2 [&>div]:tw-px-3 [&>div]:tw-py-3";
const getCreateWaveVotingLabel = (votingType: ApiWaveCreditType): string => {
  if (votingType === ApiWaveCreditType.CardSetTdh) {
    return "Memes TDH";
  }
  return WAVE_VOTING_LABELS[votingType];
};
const getApprovalThresholdTimeErrorMessage = (errors: CREATE_WAVE_VALIDATION_ERROR[]): string | undefined => {
  if (errors.includes(CREATE_WAVE_VALIDATION_ERROR.APPROVAL_THRESHOLD_TIME_EXCEEDS_WAVE_DURATION)) {
    return APPROVAL_THRESHOLD_TIME_DURATION_ERROR;
  }
  if (errors.includes(CREATE_WAVE_VALIDATION_ERROR.APPROVAL_THRESHOLD_TIME_INVALID)) {
    return APPROVAL_THRESHOLD_TIME_INVALID_ERROR;
  }
  return undefined;
};
export default function CreateWaveVoting(t0) {
  const $ = _c(78);
  const {
    waveType,
    selectedType,
    category,
    profileId,
    creditNfts,
    memeCount,
    isMemeCountLoading,
    isMemeCountError,
    allowNegativeVotes,
    maxVotesPerIdentityPerDrop,
    approvalThreshold,
    approvalThresholdTimeMs,
    errors,
    onTypeChange,
    setCategory,
    setProfileId,
    setCreditNfts,
    onAllowNegativeVotesChange,
    setMaxVotesPerIdentityPerDrop,
    setApprovalThreshold,
    setApprovalThresholdTimeMs,
    timeWeighted,
    onTimeWeightedChange
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      [ApiWaveType.Chat]: "How Drops are Rated",
      [ApiWaveType.Rank]: "How Drops are Voted",
      [ApiWaveType.Approve]: "How Drops are Voted"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const TITLES = t1;
  const [approvalTimingModeOverride, setApprovalTimingModeOverride] = useState(null);
  if (selectedType === null) {
    return null;
  }
  const timeWeightedErrorMessage = errors.includes(CREATE_WAVE_VALIDATION_ERROR.TIME_WEIGHTED_VOTING_INTERVAL_EXCEEDS_WAVE_DURATION) ? TIME_WEIGHTED_DURATION_ERROR : undefined;
  let t2;
  if ($[1] !== errors) {
    t2 = errors.includes(CREATE_WAVE_VALIDATION_ERROR.APPROVAL_THRESHOLD_REQUIRED);
    $[1] = errors;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const approvalThresholdError = t2;
  let t3;
  if ($[3] !== errors) {
    t3 = getApprovalThresholdTimeErrorMessage(errors);
    $[3] = errors;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const approvalThresholdTimeErrorMessage = t3;
  const approvalTimingErrorMessage = errors.includes(CREATE_WAVE_VALIDATION_ERROR.APPROVAL_TIMING_OPTIONS_MUTUALLY_EXCLUSIVE) ? APPROVAL_TIMING_CONFLICT_ERROR : undefined;
  const showVotingSettings = waveType !== ApiWaveType.Chat;
  let t4;
  if ($[5] !== approvalThresholdTimeMs || $[6] !== timeWeighted) {
    t4 = getCreateWaveApprovalTimingMode({
      thresholdTimeMs: approvalThresholdTimeMs,
      timeWeighted
    });
    $[5] = approvalThresholdTimeMs;
    $[6] = timeWeighted;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const inferredApprovalTimingMode = t4;
  const approvalTimingMode = approvalTimingModeOverride ?? inferredApprovalTimingMode;
  let t5;
  if ($[8] !== approvalTimingMode || $[9] !== timeWeighted) {
    t5 = approvalTimingMode === CreateWaveApprovalTimingMode.TIME_WEIGHTED ? {
      ...timeWeighted,
      enabled: true
    } : timeWeighted;
    $[8] = approvalTimingMode;
    $[9] = timeWeighted;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const timeWeightedConfig = t5;
  let t6;
  if ($[11] !== approvalThresholdTimeMs || $[12] !== onTimeWeightedChange || $[13] !== setApprovalThresholdTimeMs || $[14] !== timeWeighted) {
    t6 = mode => {
      setApprovalTimingModeOverride(mode);
      bb21: switch (mode) {
        case CreateWaveApprovalTimingMode.IMMEDIATE:
          {
            setApprovalThresholdTimeMs(null);
            onTimeWeightedChange({
              ...timeWeighted,
              enabled: false
            });
            break bb21;
          }
        case CreateWaveApprovalTimingMode.THRESHOLD_TIME:
          {
            setApprovalThresholdTimeMs(approvalThresholdTimeMs !== null && Number.isFinite(approvalThresholdTimeMs) && approvalThresholdTimeMs > 0 ? approvalThresholdTimeMs : DEFAULT_APPROVAL_THRESHOLD_TIME_MS);
            onTimeWeightedChange({
              ...timeWeighted,
              enabled: false
            });
            break bb21;
          }
        case CreateWaveApprovalTimingMode.TIME_WEIGHTED:
          {
            setApprovalThresholdTimeMs(null);
            onTimeWeightedChange({
              ...timeWeighted,
              enabled: true
            });
          }
      }
    };
    $[11] = approvalThresholdTimeMs;
    $[12] = onTimeWeightedChange;
    $[13] = setApprovalThresholdTimeMs;
    $[14] = timeWeighted;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const onApprovalTimingModeChange = t6;
  let t7;
  if ($[16] !== setApprovalThresholdTimeMs) {
    t7 = value => {
      setApprovalTimingModeOverride(CreateWaveApprovalTimingMode.THRESHOLD_TIME);
      setApprovalThresholdTimeMs(value);
    };
    $[16] = setApprovalThresholdTimeMs;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const onApprovalThresholdTimeChange = t7;
  const t8 = TITLES[waveType];
  let t9;
  if ($[18] !== t8) {
    t9 = <p className="tw-mb-0 tw-text-lg tw-font-semibold tw-text-iron-50 sm:tw-text-xl">{t8}</p>;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== onTypeChange || $[21] !== selectedType) {
    t10 = (Object.keys(VOTING_TYPES_ORDER) as ApiWaveCreditType[]).filter(_temp).map(votingType_0 => <CommonBorderedRadioButton key={votingType_0} type={votingType_0} selected={selectedType} disabled={false} variant="subtle" onChange={onTypeChange}><span className={`tw-flex tw-min-h-4 tw-items-center tw-text-sm tw-font-semibold ${selectedType === votingType_0 ? "tw-text-white" : "tw-text-iron-300 group-hover:tw-text-white"}`}>{getCreateWaveVotingLabel(votingType_0)}</span></CommonBorderedRadioButton>);
    $[20] = onTypeChange;
    $[21] = selectedType;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== category || $[24] !== errors || $[25] !== profileId || $[26] !== selectedType || $[27] !== setCategory || $[28] !== setProfileId) {
    t11 = selectedType === ApiWaveCreditType.Rep && <div className="tw-col-span-full"><CreateWaveVotingRep category={category} profileId={profileId} errors={errors} setCategory={setCategory} setProfileId={setProfileId} /></div>;
    $[23] = category;
    $[24] = errors;
    $[25] = profileId;
    $[26] = selectedType;
    $[27] = setCategory;
    $[28] = setProfileId;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== creditNfts || $[31] !== errors || $[32] !== isMemeCountError || $[33] !== isMemeCountLoading || $[34] !== memeCount || $[35] !== selectedType || $[36] !== setCreditNfts) {
    t12 = selectedType === ApiWaveCreditType.CardSetTdh && <MemeCardSetPicker creditNfts={creditNfts} memeCount={memeCount} isMemeCountLoading={isMemeCountLoading} isMemeCountError={isMemeCountError} errors={errors} onCreditNftsChange={setCreditNfts} />;
    $[30] = creditNfts;
    $[31] = errors;
    $[32] = isMemeCountError;
    $[33] = isMemeCountLoading;
    $[34] = memeCount;
    $[35] = selectedType;
    $[36] = setCreditNfts;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  let t13;
  if ($[38] !== t10 || $[39] !== t11 || $[40] !== t12) {
    t13 = <div className={VOTING_OPTIONS_GRID_CLASSES}>{t10}{t11}{t12}</div>;
    $[38] = t10;
    $[39] = t11;
    $[40] = t12;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  let t14;
  if ($[42] !== approvalThreshold || $[43] !== approvalThresholdError || $[44] !== errors || $[45] !== maxVotesPerIdentityPerDrop || $[46] !== setApprovalThreshold || $[47] !== setMaxVotesPerIdentityPerDrop || $[48] !== showVotingSettings || $[49] !== waveType) {
    t14 = showVotingSettings && <div data-testid="create-wave-voting-settings-grid" className={VOTING_SETTINGS_GRID_CLASSES}><MaxVotesPerIdentityInput value={maxVotesPerIdentityPerDrop} errors={errors} onChange={setMaxVotesPerIdentityPerDrop} />{waveType === ApiWaveType.Approve && <CreateWaveVotingThreshold threshold={approvalThreshold} error={approvalThresholdError} setThreshold={setApprovalThreshold} />}</div>;
    $[42] = approvalThreshold;
    $[43] = approvalThresholdError;
    $[44] = errors;
    $[45] = maxVotesPerIdentityPerDrop;
    $[46] = setApprovalThreshold;
    $[47] = setMaxVotesPerIdentityPerDrop;
    $[48] = showVotingSettings;
    $[49] = waveType;
    $[50] = t14;
  } else {
    t14 = $[50];
  }
  let t15;
  if ($[51] !== approvalThresholdTimeErrorMessage || $[52] !== approvalThresholdTimeMs || $[53] !== approvalTimingErrorMessage || $[54] !== approvalTimingMode || $[55] !== onApprovalThresholdTimeChange || $[56] !== onApprovalTimingModeChange || $[57] !== onTimeWeightedChange || $[58] !== timeWeightedConfig || $[59] !== timeWeightedErrorMessage || $[60] !== waveType) {
    t15 = waveType === ApiWaveType.Approve && <><CreateWaveApprovalTiming selectedMode={approvalTimingMode} errorMessage={approvalTimingErrorMessage} onModeChange={onApprovalTimingModeChange} />{approvalTimingMode === CreateWaveApprovalTimingMode.THRESHOLD_TIME && <div className="tw-mt-3" data-testid="approval-timing-detail"><CreateWaveVotingThresholdTime thresholdTimeMs={approvalThresholdTimeMs} errorMessage={approvalThresholdTimeErrorMessage} setThresholdTimeMs={onApprovalThresholdTimeChange} /></div>}{approvalTimingMode === CreateWaveApprovalTimingMode.TIME_WEIGHTED && <div className="tw-mt-3" data-testid="approval-timing-detail"><TimeWeightedVoting config={timeWeightedConfig} errorMessage={timeWeightedErrorMessage} onChange={onTimeWeightedChange} showToggle={false} /></div>}</>;
    $[51] = approvalThresholdTimeErrorMessage;
    $[52] = approvalThresholdTimeMs;
    $[53] = approvalTimingErrorMessage;
    $[54] = approvalTimingMode;
    $[55] = onApprovalThresholdTimeChange;
    $[56] = onApprovalTimingModeChange;
    $[57] = onTimeWeightedChange;
    $[58] = timeWeightedConfig;
    $[59] = timeWeightedErrorMessage;
    $[60] = waveType;
    $[61] = t15;
  } else {
    t15 = $[61];
  }
  let t16;
  if ($[62] !== allowNegativeVotes || $[63] !== onAllowNegativeVotesChange || $[64] !== waveType) {
    t16 = waveType !== ApiWaveType.Chat && <NegativeVotingToggle allowNegativeVotes={allowNegativeVotes} onChange={onAllowNegativeVotesChange} isDisabled={false} />;
    $[62] = allowNegativeVotes;
    $[63] = onAllowNegativeVotesChange;
    $[64] = waveType;
    $[65] = t16;
  } else {
    t16 = $[65];
  }
  let t17;
  if ($[66] !== onTimeWeightedChange || $[67] !== timeWeighted || $[68] !== timeWeightedErrorMessage || $[69] !== waveType) {
    t17 = waveType === ApiWaveType.Rank && <TimeWeightedVoting config={timeWeighted} errorMessage={timeWeightedErrorMessage} onChange={onTimeWeightedChange} />;
    $[66] = onTimeWeightedChange;
    $[67] = timeWeighted;
    $[68] = timeWeightedErrorMessage;
    $[69] = waveType;
    $[70] = t17;
  } else {
    t17 = $[70];
  }
  let t18;
  if ($[71] !== t13 || $[72] !== t14 || $[73] !== t15 || $[74] !== t16 || $[75] !== t17 || $[76] !== t9) {
    t18 = <div>{t9}{t13}{t14}{t15}{t16}{t17}</div>;
    $[71] = t13;
    $[72] = t14;
    $[73] = t15;
    $[74] = t16;
    $[75] = t17;
    $[76] = t9;
    $[77] = t18;
  } else {
    t18 = $[77];
  }
  return t18;
}
function _temp(votingType) {
  return VOTING_TYPES_ORDER[votingType] !== undefined;
}